import { useState } from "react";
import { HeartPulse, ClipboardCheck, CalendarClock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const steps = [
  { icon: ClipboardCheck, title: "Submit intake", desc: "Tell us your goals and background." },
  { icon: HeartPulse, title: "Clinician review", desc: "A clinician reviews your intake form." },
  { icon: CalendarClock, title: "Consult scheduled", desc: "We reach out to book your session." },
];

export default function Telehealth() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("program");
    return {
      name: "", email: "", phone: "",
      program: ["peptide_intake", "physical_training", "combined"].includes(p) ? p : "",
      goals: "", preferred_time: "", experience: "",
    };
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.program || !form.goals) {
      toast.error("Please complete all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/telehealth/bookings", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Telehealth Peptide Intake Consultations | Just Peptides Supply" description="Book a telehealth consultation for peptide intake guidance and physical training coaching with a clinician." path="/telehealth" />

      <section className="relative overflow-hidden bg-brand-ink text-white">
        <img src="https://images.unsplash.com/photo-1758691463606-1493d79cc577?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/85 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-24">
          <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-brand-teal">Telehealth</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight max-w-2xl">Peptide intake, guided by clinicians</h1>
          <p className="mt-5 max-w-xl text-white/70 text-lg">Submit a quick intake and our team will follow up to schedule your one-on-one consultation.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-14 grid gap-8 sm:grid-cols-3">
        {steps.map((s, i) => (
          <div key={s.title} className="rounded-xl border border-brand-navy/10 bg-white p-6">
            <div className="flex items-center gap-3">
              <span className="font-mono-label text-xs text-brand-teal">0{i + 1}</span>
              <s.icon className="h-5 w-5 text-brand-blue" />
            </div>
            <h3 className="mt-3 font-display text-lg text-brand-navy">{s.title}</h3>
            <p className="mt-1 text-sm text-brand-navy/55">{s.desc}</p>
          </div>
        ))}
      </section>

      <section className="mx-auto max-w-3xl px-5 lg:px-8 pb-16">
        {submitted ? (
          <div className="rounded-2xl border border-brand-teal/30 bg-brand-mist p-10 text-center fade-up" data-testid="booking-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-brand-teal" />
            <h2 className="mt-5 font-display text-3xl text-brand-navy">Intake received</h2>
            <p className="mt-3 text-brand-navy/60">Thanks, {form.name.split(" ")[0]}. Our team will reach out to {form.email} to schedule your consultation.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-brand-navy/10 bg-white p-8" data-testid="telehealth-form">
            <h2 className="font-display text-2xl text-brand-navy mb-6">Consultation intake</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label>Full name *</Label>
                <Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="th-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="th-email" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="th-phone" />
              </div>
              <div>
                <Label>Program *</Label>
                <Select value={form.program} onValueChange={(v) => setForm((f) => ({ ...f, program: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="th-program">
                    <SelectValue placeholder="Select a program">
                      {{ peptide_intake: "Peptide Intake Consultation", physical_training: "Physical Training Coaching", combined: "Combined Program" }[form.program]}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peptide_intake">Peptide Intake Consultation</SelectItem>
                    <SelectItem value="physical_training">Physical Training Coaching</SelectItem>
                    <SelectItem value="combined">Combined Program</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Your goals *</Label>
                <Textarea value={form.goals} onChange={set("goals")} className="mt-1.5" rows={3} placeholder="What are you looking to achieve?" data-testid="th-goals" />
              </div>
              <div>
                <Label>Preferred time</Label>
                <Input value={form.preferred_time} onChange={set("preferred_time")} className="mt-1.5" placeholder="e.g. Weekday mornings" data-testid="th-time" />
              </div>
              <div>
                <Label>Experience level</Label>
                <Input value={form.experience} onChange={set("experience")} className="mt-1.5" placeholder="e.g. Beginner" data-testid="th-experience" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="th-submit" className="mt-7 w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-12 text-base">
              {loading ? "Submitting…" : "Request Consultation"}
            </Button>
            <p className="mt-4 font-mono-label text-[11px] uppercase tracking-[0.12em] text-brand-navy/40 text-center">
              Consultations are informational and for research guidance only.
            </p>
          </form>
        )}
      </section>
    </>
  );
}
